import { format, parseISO } from "date-fns";
import { buildInvoiceShareText } from "./utils";

export const PER_PAGE = 4;
const MAX_PT = 14;
const MIN_PT = 7;

const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

// Turn one share-text line into print HTML: *bold* lines become headings,
// "Label: value" lines become a two-column row.
function lineHtml(line) {
  const bold = line.match(/^\*(.+)\*$/);
  if (bold) return `<div class="h">${esc(bold[1])}</div>`;
  if (!line.trim()) return `<div class="gap"></div>`;
  if (line.trim() === "Not provided") return `<div class="row"><span class="k"></span><span class="v">${esc(line.trim())}</span></div>`;
  const m = line.match(/^([A-Za-z ]+):(?:\s(.*))?$/);
  if (m) return `<div class="row"><span class="k">${esc(m[1])}</span><span class="v">${esc(m[2] ?? "")}</span></div>`;
  return `<div class="v cont">${esc(line)}</div>`;
}

// Print-only: drop the Google Maps block (label + URL line); WhatsApp share text is untouched.
// Print-only: add the internal delivery date (home delivery) right after Fulfilment.
export function printLines(r) {
  const out = [];
  const lines = buildInvoiceShareText(r).split("\n");
  for (let i = 0; i < lines.length; i++) {
    if (lines[i] === "Google Maps Location:") { i += 1; continue; }
    if (!lines[i].trim() && out.length && !out[out.length - 1].trim()) continue;
    out.push(lines[i]);
    if (lines[i].startsWith("Fulfilment:") && r.fulfillment_type !== "store_pickup" && r.internal_delivery_date) {
      let d = r.internal_delivery_date;
      try { d = format(parseISO(r.internal_delivery_date), "d MMMM yyyy"); } catch { /* keep raw */ }
      out.push(`Delivery Date: ${d}`);
    }
  }
  return out;
}

export const pageCount = (n) => Math.ceil(n / PER_PAGE);

export function buildInvoicesPrintHtml(rows, groupLabel = "") {
  const printedAt = new Date().toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  const list = rows || [];
  const pages = [];
  for (let p = 0; p < pageCount(list.length); p++) {
    const cells = list.slice(p * PER_PAGE, p * PER_PAGE + PER_PAGE).map((r) => (
      `<article class="cell" data-invoice="${esc(r.invoice_number)}"><div class="fit" style="font-size:${MAX_PT}pt">${printLines(r).map(lineHtml).join("")}</div></article>`
    ));
    while (cells.length < PER_PAGE) cells.push(`<article class="cell empty"></article>`);
    const total = pageCount(list.length);
    pages.push(`<section class="page"><header><div class="brand">Tanish Textiles${groupLabel ? ` · ${esc(groupLabel)}` : ""}</div><div class="meta">Fulfilment details · Page ${p + 1} of ${total}</div></header><div class="grid">${cells.join("")}</div><footer><span>Printed ${esc(printedAt)}</span><span>${list.length} invoice${list.length === 1 ? "" : "s"} · 4 per sheet · Page ${p + 1} of ${total}</span></footer></section>`);
  }
  return `<!doctype html><html><head><meta charset="utf-8"><title>Invoices (${list.length})</title><style>
    @page { size: A4 portrait; margin: 7mm; }
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; background: #fff; color: #1f1a17; font-family: -apple-system, "Helvetica Neue", Arial, sans-serif; }
    .page { width: 196mm; height: 283mm; display: flex; flex-direction: column; page-break-after: always; break-after: page; overflow: hidden; }
    .page:last-child { page-break-after: auto; break-after: auto; }
    header, footer { flex: 0 0 auto; display: flex; justify-content: space-between; align-items: baseline; gap: 2mm 6mm; }
    header { border-bottom: 0.5mm solid #6a1f2b; padding-bottom: 1.2mm; margin-bottom: 1.5mm; }
    footer { border-top: 0.25mm solid #b9ada3; padding-top: 1.2mm; margin-top: 1.5mm; font-size: 7.5pt; color: #6b625b; }
    .brand { font-family: Georgia, "Times New Roman", serif; font-size: 13pt; color: #6a1f2b; letter-spacing: .02em; white-space: nowrap; }
    .meta { font-size: 8pt; color: #6b625b; white-space: nowrap; }
    .grid { flex: 1 1 auto; min-height: 0; display: grid; grid-template-columns: 1fr 1fr; grid-template-rows: 1fr 1fr; }
    .cell { padding: 3.5mm 4mm; overflow: hidden; border-right: 0.25mm dashed #b9ada3; border-bottom: 0.25mm dashed #b9ada3; min-height: 0; display: flex; flex-direction: column; justify-content: center; }
    .cell:nth-child(2n) { border-right: 0; }
    .cell:nth-child(n+3) { border-bottom: 0; }
    .fit { line-height: 1.32; }
    .h { font-weight: 700; font-size: 1.3em; margin: 0 0 0.15em; line-height: 1.2; }
    .h + .h { font-size: 1.05em; color: #4a4039; }
    .row { display: grid; grid-template-columns: 34% 1fr; gap: 0 0.6em; padding: 0.24em 0; border-bottom: 0.2mm solid #e6dfd6; }
    .k { color: #6b625b; }
    .v { white-space: pre-wrap; word-break: break-word; overflow-wrap: anywhere; }
    .cont { padding: 0.1em 0 0.24em calc(34% + 0.6em); border-bottom: 0.2mm solid #e6dfd6; }
    .gap { height: 0.45em; }
    @media print { .page { width: auto; height: 100vh; } }
    @media screen { body { padding: 16px; background: #f3efe9; } .page { background: #fff; padding: 7mm; width: 210mm; height: 297mm; margin: 0 auto 16px; box-shadow: 0 2px 12px rgba(0,0,0,.08); } }
  </style></head><body>${pages.join("")}</body></html>`;
}

// Shrink each invoice's type until it fits its quarter; report any that still overflow at MIN_PT.
function fitCells(doc) {
  const overflow = [];
  doc.querySelectorAll(".cell:not(.empty)").forEach((cell) => {
    const fit = cell.querySelector(".fit");
    const cs = doc.defaultView.getComputedStyle(cell);
    const avail = cell.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
    let pt = MAX_PT;
    fit.style.fontSize = `${pt}pt`;
    while (fit.scrollHeight > avail && pt > MIN_PT) {
      pt = Math.max(MIN_PT, +(pt - 0.5).toFixed(1));
      fit.style.fontSize = `${pt}pt`;
    }
    if (fit.scrollHeight > avail + 1) overflow.push(cell.dataset.invoice);
  });
  return overflow;
}

// Renders the sheets into a hidden, full-size iframe (so measurement works), fits text,
// and returns a handle: { pages, overflow, print, cancel }. print() must be called from a
// user gesture (the confirmation button) so iOS Safari shows the native AirPrint sheet.
export function preparePrint(rows, groupLabel) {
  const old = document.getElementById("invoice-print-frame");
  if (old) old.remove();
  const frame = document.createElement("iframe");
  frame.id = "invoice-print-frame";
  frame.setAttribute("aria-hidden", "true");
  frame.style.cssText = "position:fixed;left:-20000px;top:0;width:210mm;height:297mm;border:0;opacity:0;pointer-events:none;";
  document.body.appendChild(frame);
  const doc = frame.contentWindow.document;
  doc.open();
  doc.write(buildInvoicesPrintHtml(rows, groupLabel));
  doc.close();
  const overflow = fitCells(doc);
  const win = frame.contentWindow;
  const remove = () => setTimeout(() => frame.remove(), 1000);
  win.addEventListener("afterprint", remove);
  return {
    count: rows.length,
    pages: pageCount(rows.length),
    overflow,
    print: () => { win.focus(); win.print(); },
    cancel: () => frame.remove(),
  };
}
